import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Ruler, Scissors, Layers, Droplets, CheckSquare, Package } from "lucide-react";
import { Seo } from "@/components/Seo";
import { IMAGES } from "@/data/products";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const CAPABILITIES = [
  { icon: Ruler, title: "Pattern Making & Sampling", text: "Patterns and samples developed from your tech pack or reference garment, refined until approval." },
  { icon: Layers, title: "Cutting", text: "Marker planning and cutting handled in-house for consistent panels and controlled wastage." },
  { icon: Scissors, title: "Stitching & Sewing", text: "Dedicated denim sewing lines for five-pocket jeans across women's, men's and kids' fits." },
  { icon: Droplets, title: "Washing & Finishing", text: "Rinse, stone, enzyme and fashion washes developed to match your approved reference." },
  { icon: CheckSquare, title: "Quality Control", text: "Inline and final inspection against your approved sample and measurement chart." },
  { icon: Package, title: "Packing & Dispatch", text: "Buyer-specified labelling, polybagging, cartons and dispatch documentation." },
];

export default function Capabilities() {
  return (
    <div data-testid="capabilities-page">
      <Seo
        title="Manufacturing Capabilities — Shineni India Denim, Faridabad"
        description="Pattern making, cutting, stitching, washing, finishing, quality control and packing — denim jeans manufacturing under one roof in Faridabad."
      />

      <section className="relative border-b border-border">
        <img src={IMAGES.factory} alt="Denim manufacturing floor — sample imagery" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-indigo-deep/70" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Under one roof · Faridabad</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-semibold text-white sm:text-5xl">
            Manufacturing capabilities
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            From pattern to packed carton, every stage of your denim program is handled by our own
            production team. Share your tech pack to discuss machinery, lines and timelines for
            your specific order.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="capabilities-grid">
          {CAPABILITIES.map((c) => (
            <motion.div key={c.title} {...fade} className="border border-border bg-white p-8">
              <c.icon className="h-7 w-7 text-gold" />
              <h2 className="mt-5 font-display text-xl font-semibold text-indigo">{c.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground/70">{c.text}</p>
            </motion.div>
          ))}
        </div>

        <motion.div {...fade} className="mt-14 grid gap-8 rounded-sm border border-border bg-white p-8 sm:p-12 lg:grid-cols-2 lg:items-center">
          <div>
            <h2 className="font-display text-2xl font-semibold text-indigo sm:text-3xl">Built around your tech pack</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground/70">
              We don't publish fixed capacity or machine counts because every program is different —
              a 500-piece boutique order and a multi-container program need different lines.
              Send your specifications and we will respond with what we can commit to, in writing.
            </p>
            <Link to="/request-a-quote" data-testid="capabilities-quote-cta"
              className="mt-8 inline-block rounded-sm bg-gold px-8 py-3.5 text-sm font-semibold text-indigo-deep transition-colors hover:bg-gold-dark">
              Discuss Your Program
            </Link>
          </div>
          <div>
            <img src={IMAGES.sewing} alt="Close-up of denim stitching — sample imagery" className="aspect-[4/3] w-full rounded-sm border border-border object-cover" />
            <p className="mt-2 text-xs text-muted-foreground">Representative sample imagery</p>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
